import React from "react";
import ErrorPage from "../components/pages/error/ErrorPage";
import { AppContainer } from "../containers/App";
import { ErrorContainer } from "../containers/Error";
import { UserContainer } from "../containers/User";

type Props = { children: React.ReactNode };
type ErrorHandler = (error: Error, info: React.ErrorInfo) => void;
type ErrorHandlingComponent<Props> = (
  props: Props,
  error?: Error
) => React.ReactNode;
type ErrorState = { error?: Error };

/**
 * 子コンポーネントのエラーを補足するための Wrap API
 *
 * Error Boundary に必要な getDerivedStateFromError や
 * componentDidCatch が Functional Component には実装されていないため Wrap API を用意する
 *
 * @param component 子コンポーネント
 * @param errorHandler エラーハンドリング処理
 */
function Catch<Props extends {}>(
  component: ErrorHandlingComponent<Props>,
  errorHandler?: ErrorHandler
): React.ComponentType<Props> {
  function Inner(props: { error?: Error; props: Props }) {
    return (
      <React.Fragment>{component(props.props, props.error)}</React.Fragment>
    );
  }

  return class extends React.Component<Props, ErrorState> {
    state: ErrorState = {
      error: undefined,
    };

    static getDerivedStateFromError(error: Error) {
      return { error };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
      if (errorHandler) {
        errorHandler(error, info);
      }
    }

    render() {
      return <Inner error={this.state.error} props={this.props} />;
    }
  };
}

/**
 * エラー画面表示用 Boundary コンポーネント
 * @param props
 */
const ErrorBoundary = Catch((props: Props, error?: Error) => {
  const appContainer = AppContainer.useContainer();
  const errorContainer = ErrorContainer.useContainer();
  const hasError = errorContainer.hasError();
  const user = UserContainer.useContainer().user;

  if (hasError) {
    // ErrorContainer が Error を保持している場合
    // 主に非同期処理におけるエラー

    // ローディング状態をリセット
    appContainer.setLoading(false);
    return <ErrorPage></ErrorPage>;
  }

  if (error) {
    // 引数の error が渡された場合
    // 主に各コンポーネントの render 時のエラー
    // ErrorContainer の state を変化させ、ErrorPage を表示する
    ErrorContainer.useContainer().setDefaultError(user.id || user.rawId, error);
    return;
  }

  return <React.Fragment>{props.children}</React.Fragment>;
});

export default ErrorBoundary;
